import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { chatBotIcon } from '@assets/images';
import { ChatbotAuthStatus, ChatbotMessage, ChatbotSessionStatus } from '@shared/hooks';
import { TypingDots } from './TypingDots';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { CloseButton } from '../Dialog';
import { ChatBotMessage } from './ChatBotMessage';
import { useSharedTFunction } from '@shared/i18n';
import { SystemStyleObject } from '@mui/system';

interface Props {
  messages: ChatbotMessage[];
  sendMessage: (text: string) => void;
  authStatus: ChatbotAuthStatus;
  sessionStatus: ChatbotSessionStatus;
  onCloseChat: () => void;
  initialMessage?: string;
  sx?: SystemStyleObject;
}

export const ChatbotChat = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      messages,
      sendMessage,
      authStatus,
      sessionStatus,
      onCloseChat,
      initialMessage,
      sx = {},
      ...props
    },
    ref
  ) => {
    const t = useSharedTFunction();
    const [input, setInput] = useState('');

    useEffect(() => {
      if (initialMessage) {
        setInput(initialMessage);
      }
    }, [initialMessage]);

    const handleSend = useCallback(() => {
      if (!input.trim()) return;
      sendMessage(input);
      setInput('');
    }, [input, sendMessage]);

    const isChatReady = authStatus === 'ready' && sessionStatus === 'ready';
    const isChatInitiating = authStatus === 'initiating' || sessionStatus === 'initiating';

    return (
      <Box
        ref={ref}
        sx={{
          width: 500,
          height: 700,
          bgcolor: 'background.paper',
          boxShadow: '0px 8px 24px rgba(0,0,0,0.1)',
          position: 'relative',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          zIndex: ({ zIndex }) => zIndex.modal + 1,
          ...sx,
        }}
        {...props}
      >
        <Backdrop
          sx={{
            position: 'absolute',
            borderRadius: 2,
            zIndex: (theme) => theme.zIndex.modal + 2,
            bgcolor: 'rgba(0,0,0,0.1)',
          }}
          open={isChatInitiating}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <CloseButton onClick={onCloseChat} aria-label={t('shared.close')}>
          <CloseRoundedIcon fontSize={'small'} />
        </CloseButton>
        <Box
          sx={{
            p: 1.5,
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Avatar
            src={chatBotIcon}
            sx={{
              width: 40,
              height: 40,
            }}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            {t('shared.chatbot.botName')}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: 1,
          }}
        >
          {messages
            .slice()
            .reverse()
            .map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: msg.user.type === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'end',
                  gap: 1,
                }}
              >
                {msg.user.type === 'bot' ? (
                  <Avatar
                    src={chatBotIcon}
                    sx={{
                      width: 32,
                      height: 32,
                    }}
                  />
                ) : (
                  <Avatar alt={msg.user.name} sx={{ width: 32, height: 32 }}>
                    <Typography variant="body2">
                      {getUserCredentialString(msg.user.name)}
                    </Typography>
                  </Avatar>
                )}

                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor: msg.user.type === 'user' ? 'primary.main' : 'divider',
                    borderRadius: 1.5,
                    maxWidth: '85%',
                    color: msg.user.type === 'user' ? 'white' : 'text',
                  }}
                >
                  {msg.type === 'text' ? (
                    <ChatBotMessage message={msg.text} userType={msg.user.type} />
                  ) : (
                    <TypingDots />
                  )}
                </Paper>
              </Box>
            ))}
        </Box>

        <Box sx={{ p: 1, display: 'flex', borderTop: '1px solid #eee' }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={t('shared.chatbot.typeAQuestion')}
            value={input}
            autoFocus
            disabled={!isChatReady}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <IconButton color="primary" onClick={handleSend} disabled={!isChatReady}>
            <SendIcon />
          </IconButton>
        </Box>
        <Typography fontSize="12px" color="text.disabled" gutterBottom sx={{ alignSelf: 'center' }}>
          {t('shared.chatbot.disclaimer')}
        </Typography>
      </Box>
    );
  }
);

ChatbotChat.displayName = 'ChatbotChat';

const getUserCredentialString = (userName: string) => {
  const [firstName, lastName] = userName.split(' ');
  if (lastName) return `${firstName[0]}${lastName[0]}`;
  return `${firstName[0]}`;
};
